type ApiRequestOptions = {
  method: "GET" | "POST" | "DELETE";
  accessToken: string | null | undefined;
  url: string;
  body?: BodyInit | null;
  headers?: Record<string, string>;
  signal?: AbortSignal;
};

const manufacturerBaseUrl = import.meta.env.VITE_URL_MANUFACTURER as string;

const notifyUnauthorized = (res: Response) => {
  if (res.status === 401) {
    const customEvent = new CustomEvent("unauthorized_call");
    document.dispatchEvent(customEvent);
  }
};

const getErrorMessage = async (res: Response) => {
  try {
    const data: unknown = await res.json();
    if (typeof data === "string") return data;
    if (data && typeof data === "object") {
      const record = data as Record<string, unknown>;
      if (typeof record.title === "string") return record.title;
      if (typeof record.message === "string") return record.message;
      if (typeof record.error === "string") return record.error;
    }
  } catch {
    // ignore
  }

  return `${res.status} ${res.statusText}`.trim();
};

const apiRequest = async <T>({ method, accessToken, url, body, headers, signal }: ApiRequestOptions): Promise<T> => {
  const res = await fetch(url, {
    method,
    body,
    signal,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...(body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...headers,
    },
  });

  notifyUnauthorized(res);

  if (!res.ok) {
    throw new Error(await getErrorMessage(res));
  }

  const contentType = res.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return (await res.text()) as unknown as T;
  }

  return (await res.json()) as T;
};

const joinUrl = (base: string, path: string) => `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;

export const getManufacturerProductImages = async (args: {
  manufacturerId: string;
  sku: string;
  accessToken: string | null | undefined;
  signal?: AbortSignal;
}) => {
  const url = joinUrl(
    manufacturerBaseUrl,
    `${encodeURIComponent(args.manufacturerId)}/Product/${encodeURIComponent(args.sku)}/Images`,
  );
  return apiRequest<unknown>({ method: "GET", url, accessToken: args.accessToken, signal: args.signal });
};

export const uploadManufacturerProductImages = async (args: {
  manufacturerId: string;
  sku: string;
  files: File[];
  accessToken: string | null | undefined;
}) => {
  const url = joinUrl(
    manufacturerBaseUrl,
    `${encodeURIComponent(args.manufacturerId)}/Product/${encodeURIComponent(args.sku)}/Images`,
  );
  const formData = new FormData();
  for (const file of args.files) {
    formData.append("files", file);
  }
  if (args.files.length === 1) {
    formData.append("file", args.files[0]);
  }

  return apiRequest<unknown>({ method: "POST", url, accessToken: args.accessToken, body: formData });
};

export const setManufacturerProductImagePrimary = async (args: {
  manufacturerId: string;
  sku: string;
  imageId: string;
  accessToken: string | null | undefined;
}) => {
  const url = joinUrl(
    manufacturerBaseUrl,
    `${encodeURIComponent(args.manufacturerId)}/Product/${encodeURIComponent(args.sku)}/Images/${encodeURIComponent(args.imageId)}/Primary`,
  );
  return apiRequest<unknown>({ method: "POST", url, accessToken: args.accessToken });
};

export const reorderManufacturerProductImages = async (args: {
  manufacturerId: string;
  sku: string;
  imageIdsInOrder: string[];
  accessToken: string | null | undefined;
}) => {
  const url = joinUrl(
    manufacturerBaseUrl,
    `${encodeURIComponent(args.manufacturerId)}/Product/${encodeURIComponent(args.sku)}/Images/Reorder`,
  );
  const body = JSON.stringify({
    imageIds: args.imageIdsInOrder,
    orderedImageIds: args.imageIdsInOrder,
  });
  return apiRequest<unknown>({ method: "POST", url, accessToken: args.accessToken, body });
};

export const deleteManufacturerProductImage = async (args: {
  manufacturerId: string;
  sku: string;
  imageId: string;
  accessToken: string | null | undefined;
}) => {
  const url = joinUrl(
    manufacturerBaseUrl,
    `${encodeURIComponent(args.manufacturerId)}/Product/${encodeURIComponent(args.sku)}/Images/${encodeURIComponent(args.imageId)}`,
  );
  return apiRequest<unknown>({ method: "DELETE", url, accessToken: args.accessToken });
};
